/**
 * Cliente HTTP centralizado para endpoints /api/v2/*.
 * Refactor multi-tenancy: workspaces, marcas (brands), OAuth identities, social_connections.
 */
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';
const V2 = `${API}/v2`;

const getToken = () => localStorage.getItem('token') || '';

const headers = () => ({
  Authorization: `Bearer ${getToken()}`,
  'Content-Type': 'application/json',
});

// ============ WORKSPACES ============

export const workspacesApi = {
  list: async () => {
    const r = await axios.get(`${V2}/ws`, { headers: headers() });
    return r.data;
  },
  get: async (workspaceId) => {
    const r = await axios.get(`${V2}/ws/${workspaceId}`, { headers: headers() });
    return r.data;
  },
  create: async ({ name, plan_tier = 'free' }) => {
    const r = await axios.post(`${V2}/ws`, { name, plan_tier }, { headers: headers() });
    return r.data;
  },
  update: async (workspaceId, updates) => {
    const r = await axios.patch(`${V2}/ws/${workspaceId}`, updates, { headers: headers() });
    return r.data;
  },
  remove: async (workspaceId) => {
    await axios.delete(`${V2}/ws/${workspaceId}`, { headers: headers() });
  },
  listMembers: async (workspaceId) => {
    const r = await axios.get(`${V2}/ws/${workspaceId}/members`, { headers: headers() });
    return r.data;
  },
  invite: async (workspaceId, { email, role = 'member' }) => {
    const r = await axios.post(`${V2}/ws/${workspaceId}/invitations`, { email, role }, { headers: headers() });
    return r.data;
  },
  acceptInvitation: async (token) => {
    const r = await axios.post(`${V2}/ws/invitations/${token}/accept`, {}, { headers: headers() });
    return r.data;
  },
  removeMember: async (workspaceId, userId) => {
    await axios.delete(`${V2}/ws/${workspaceId}/members/${userId}`, { headers: headers() });
  },
};

// ============ BRANDS (marcas) ============

export const brandsApi = {
  list: async (workspaceId, includeArchived = false) => {
    const r = await axios.get(`${V2}/brands`, {
      params: { workspace_id: workspaceId, include_archived: includeArchived },
      headers: headers(),
    });
    return r.data;
  },
  get: async (brandId) => {
    const r = await axios.get(`${V2}/brands/${brandId}`, { headers: headers() });
    return r.data;
  },
  create: async (workspaceId, data) => {
    const r = await axios.post(`${V2}/brands`, data, {
      params: { workspace_id: workspaceId },
      headers: headers(),
    });
    return r.data;
  },
  update: async (brandId, updates) => {
    const r = await axios.patch(`${V2}/brands/${brandId}`, updates, { headers: headers() });
    return r.data;
  },
  archive: async (brandId) => {
    const r = await axios.post(`${V2}/brands/${brandId}/archive`, {}, { headers: headers() });
    return r.data;
  },
  restore: async (brandId) => {
    const r = await axios.post(`${V2}/brands/${brandId}/restore`, {}, { headers: headers() });
    return r.data;
  },
};

// ============ OAUTH IDENTITIES ============

export const oauthApi = {
  listIdentities: async (workspaceId, provider = null) => {
    const params = { workspace_id: workspaceId };
    if (provider) params.provider = provider;
    const r = await axios.get(`${V2}/oauth/identities`, { params, headers: headers() });
    return r.data;
  },
  revokeIdentity: async (identityId) => {
    await axios.delete(`${V2}/oauth/identities/${identityId}`, { headers: headers() });
  },
  // Meta-specific
  metaStart: async (workspaceId, returnUrl = null) => {
    const params = { workspace_id: workspaceId };
    if (returnUrl) params.return_url = returnUrl;
    const r = await axios.get(`${V2}/oauth/meta/start`, { params, headers: headers() });
    return r.data; // { auth_url, state }
  },
  metaListAccounts: async (identityId) => {
    const r = await axios.get(`${V2}/oauth/meta/identities/${identityId}/accounts`, { headers: headers() });
    return r.data; // { ad_accounts, pages, instagram_accounts }
  },
};

// ============ SOCIAL CONNECTIONS ============

export const connectionsApi = {
  list: async (brandId, { service = null, onlyActive = true } = {}) => {
    const params = { client_id: brandId, only_active: onlyActive };
    if (service) params.service = service;
    const r = await axios.get(`${V2}/connections`, { params, headers: headers() });
    return r.data;
  },
  create: async ({ client_id, oauth_identity_id, service, external_account_id, external_account_name, metadata }) => {
    const r = await axios.post(`${V2}/connections`, {
      client_id, oauth_identity_id, service, external_account_id, external_account_name, metadata,
    }, { headers: headers() });
    return r.data;
  },
  disconnect: async (connectionId) => {
    await axios.delete(`${V2}/connections/${connectionId}`, { headers: headers() });
  },
};

export default {
  workspaces: workspacesApi,
  brands: brandsApi,
  oauth: oauthApi,
  connections: connectionsApi,
};
